import { ArrowRight, Sprout, Briefcase, Rocket } from "lucide-react";

const PATHS = [
  {
    key: "elementary",
    title: "Lemonade Leaders Jr.",
    grades: "Elementary · Grades 2–5",
    icon: Sprout,
    accent: "from-[#FFD500] to-[#FFE680]",
    chip: "bg-[#FFD500] text-black",
    skills: ["My First Idea", "Coin Counter Game", "Sign Designer", "Junior CEO Certificate"],
    outcome: "A kid-friendly mini-business and a printable Junior CEO certificate.",
    ctaHref: "/lemonade-leaders-jr",
    ctaLabel: "Explore the Demo",
    testId: "path-elementary",
  },
  {
    key: "middle",
    title: "Business Builder Journey",
    grades: "Middle School · Grades 6–8",
    icon: Briefcase,
    accent: "from-[#00E65C] to-[#86EFAC]",
    chip: "bg-[#00E65C] text-black",
    skills: ["DISC Birds Profile", "Mini Business Plan", "Brand & Marketing", "First Real Pitch"],
    outcome: "A complete Mini Business Plan v1 (PDF) and a 60-second pitch.",
    ctaHref: "/register?role=student",
    ctaLabel: "Start Building",
    testId: "path-middle",
  },
  {
    key: "high",
    title: "Future Founder Pathway",
    grades: "High School · Grades 9–12",
    icon: Rocket,
    accent: "from-[#FFAA00] to-[#FFD500]",
    chip: "bg-[#FFAA00] text-black",
    skills: ["Deeper Financial Literacy", "Market Validation", "Investor-Style Pitch", "Workforce Skills"],
    outcome: "Investor-ready plan, financial model, and a workforce-ready resume add.",
    ctaHref: "/register?role=student",
    ctaLabel: "Become a Founder",
    testId: "path-high",
  },
];

/**
 * LearningPathsSection — three K-12 pathways as bold colored cards.
 * Each card has its own brand tint (yellow / green / gold).
 */
export default function LearningPathsSection({ onSelectPath }) {
  return (
    <section
      id="paths"
      className="relative bg-white py-20 sm:py-28"
      data-testid="learning-paths-section"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs font-black tracking-[0.22em] uppercase text-gray-500 mb-3">
            Choose Your Journey
          </p>
          <h2 className="text-[32px] md:text-5xl font-black tracking-tighter leading-[1.05] text-black">
            One Program.{" "}
            <span className="lsbp-yellow-chip">Three Pathways.</span>
          </h2>
          <p className="mt-4 text-base md:text-xl text-gray-700">
            Designed to grow with students from elementary curiosity to
            future-founder ambition.
          </p>
        </div>

        {/* Pathways feature illustration */}
        <div className="max-w-5xl mx-auto mb-10 rounded-3xl overflow-hidden ring-1 ring-black/10 shadow-[0_18px_40px_rgba(0,0,0,0.08)]">
          <img
            src="/assets/landing/learning-pathways.png"
            alt="Three K-12 learning pathways shown side by side — Elementary Lemonade Leaders Jr., Middle School Business Builder Journey, and High School Future Founder Pathway."
            className="w-full h-auto block"
            loading="lazy"
            data-testid="learning-paths-feature-image"
          />
        </div>

        <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
          {PATHS.map((p) => (
            <article
              key={p.key}
              className="group relative bg-white ring-1 ring-black/10 rounded-3xl overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(255,213,0,0.18)]"
              data-testid={p.testId}
            >
              {/* Accent header */}
              <div className={`relative h-32 bg-gradient-to-br ${p.accent} p-5 flex items-end`}>
                <div className="absolute top-4 right-4 text-4xl lsbp-float select-none" aria-hidden="true">🍋</div>
                <p className={`inline-flex items-center gap-1.5 ${p.chip} px-2.5 py-1 rounded-full text-[10px] font-black tracking-wide uppercase`}>
                  <p.icon className="w-3 h-3" /> {p.grades}
                </p>
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <h3 className="text-2xl font-black text-black tracking-tight mb-1">
                  {p.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4">{p.outcome}</p>
                <ul className="space-y-1.5 mb-6 flex-1">
                  {p.skills.map((s) => (
                    <li key={s} className="flex items-center gap-2 text-sm text-gray-800">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FFD500]" aria-hidden="true" />
                      {s}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => onSelectPath(p)}
                  className="mt-auto inline-flex items-center justify-between gap-2 bg-black text-white px-4 py-3 rounded-full text-lg font-bold w-full group-hover:bg-[#FFD500] group-hover:text-black transition-colors"
                  data-testid={`${p.testId}-cta`}
                >
                  {p.ctaLabel}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
